import { Link } from "react-router-dom";
import { ShieldCheck, Sparkle } from "@phosphor-icons/react";

/**
 * Product Registry — genuine products with security features / distinguishing characteristics.
 * Distinct from /products (Suspect Products repository).
 * Used for: investigator training material + cross-case linking (same security feature
 * absent in two seizures = likely same counterfeit source).
 *
 * Schema (planned, not yet shipped):
 *   product_registry_id · client_id · sku · brand · category
 *   security_features: [{ name, description, image_url, training_notes }]
 *   distinguishing_marks: [{ type, location, image_url }]
 *   genuine_packaging_specs · serialization_scheme · qr_pattern
 *   training_video_url · training_pdf_url
 */
export default function ProductRegistry() {
  return (
    <div className="space-y-5 max-w-3xl" data-testid="product-registry-page">
      <div>
        <div className="label-eyebrow">Intelligence · Repository</div>
        <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Product Registry</h1>
        <div className="text-[12px] text-[var(--span-text-2)] mt-1">
          Catalog of genuine products with security features &amp; distinguishing characteristics — used for investigator
          training and cross-case source linking.
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-[var(--span-accent,#004EEB)]/10 flex items-center justify-center shrink-0">
            <ShieldCheck size={22} className="text-[var(--span-accent,#004EEB)]" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="font-display font-bold text-[18px]">Coming next sprint</h2>
              <span className="badge badge-info">SCHEMA DESIGN</span>
            </div>
            <p className="text-[13px] text-[var(--span-text-2)] mt-2 leading-relaxed">
              The schema is being finalized with the CEO. It captures per-SKU security features
              (holograms, micro-text, QR/serial schemes, packaging specs) plus high-resolution
              training images for field investigators.
            </p>
            <ul className="text-[12px] text-[var(--span-text-2)] mt-3 space-y-1 list-disc list-inside">
              <li>Per-SKU security feature catalog with training images</li>
              <li>Distinguishing marks (location, type, photo)</li>
              <li>Serialization scheme &amp; QR pattern documentation</li>
              <li>Cross-case linking: if the same anti-counterfeit feature is missing in
                  seizures across cities, flag as likely common source</li>
              <li>Training PDF / video attachments per product</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="card-flat p-5 flex items-start gap-3 border-l-2 border-[var(--span-accent,#004EEB)]">
        <Sparkle size={16} className="mt-0.5 text-[var(--span-accent,#004EEB)] shrink-0" weight="fill" />
        <div className="text-[12.5px] flex-1">
          For now, suspect / counterfeit product references live in{" "}
          <Link to="/products" className="underline font-medium">Suspect Products</Link>.
        </div>
      </div>
    </div>
  );
}
